import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Spinner,
  Checkbox,
  Label,
  Radio,
  RadioGroup,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Avatar,
} from '@fluentui/react-components';
import {
  DismissRegular,
  FilterRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import { Module } from '../models/module';
import { Tender, Tendering, TenderManagerTool as TenderManagerToolModel } from '../models/tendering';
import { Tag as TagModel } from '../models/tag';
import { getTenderingDocuments, getTenderManagerDocuments } from '../services/tenderingService';
import { getUsersByModule } from '../services/usersService';
import { getAllTags } from '../services/tagService';
import TenderingManagerFilterDialog from './dialogs/TenderingManagerFilterDialog';
import TenderContactDrawer from './drawers/TenderContactDrawer';
import TenderSettingsDrawer from './TenderSettings/TenderSettingsDrawer';
import NewNote from './TenderSettings/Dialogs/NewNote';
import { createNote } from '../services/noteService';
import { updateTenderManagerTool } from '../services/tenderingService';

interface User {
  userID: number;
  displayName: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  color: string;
}

interface FilterChip {
  key: string;
  name: string;
}



export const TenderingManagerTool: React.FC = () => {
  const [loaded, setLoaded] = useState(false);
  const [loadingTenderings, setLoadingTenderings] = useState(false);
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [tenderings, setTenderings] = useState<Tendering[]>([]);
  const [tenders, setTenders] = useState<TenderManagerToolModel[]>([]);
  const [tenderManagerToolList, setTenderManagerToolList] = useState<TenderManagerToolModel[]>([]);
  const [filteredData, setFilteredData] = useState<TenderManagerToolModel[]>([]);
  const [filterChips, setFilterChips] = useState<string[]>([]);
  const [savedFilters, setSavedFilters] = useState<string[]>([]);
  const [showSaveFilters, setShowSaveFilters] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [selectedFontSize, setSelectedFontSize] = useState('12px');
  const [users, setUsers] = useState<User[]>([]);
  const [tags, setTags] = useState<TagModel[]>([]);
  const [showUnpublished, setShowUnpublished] = useState(false);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [selectedTender, setSelectedTender] = useState<TenderManagerToolModel | null>(null);
  const [modalOption, setModalOption] = useState<string | undefined>(undefined);
  const [showTenderDrawer, setShowTenderDrawer] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [filterColumn, setFilterColumn] = useState<string>('');
  const [filterInputValue, setFilterInputValue] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showPrioNoteDialog, setShowPrioNoteDialog] = useState(false);
  const [selectedTenderForPrio, setSelectedTenderForPrio] = useState<TenderManagerToolModel | null>(null);

  const columnMap: { [key: string]: string } = {
    'Ramavtal': 'tendering',
    'Avtalspart': 'tenderName',
    'Koncern': 'organization',
    'Besöksadress': 'adress',
    'Kontaktperson': 'contactName',
    'Prio': 'prio',
    'Handläggare': 'tenderingManager',
    'Planerad': 'planned',
    'Uppföljd': 'followedUp',
    'Statusetiketter': 'statuses',
    'Org.nr': 'organizationNr',
  };

  const columnDefinitions = [
    { name: 'Ramavtal', key: 'tendering' },
    { name: 'Avtalspart', key: 'tenderName' },
    { name: 'Koncern', key: 'organization' },
    { name: 'Besöksadress', key: 'adress' },
    { name: 'Kontaktperson', key: 'contactName' },
    { name: 'Prio', key: 'prio' },
    { name: 'Handläggare', key: 'tenderingManager' },
    { name: 'Planerad', key: 'planned' },
    { name: 'Uppföljd', key: 'followedUp' },
    { name: 'Statusetiketter', key: 'statuses' },
  ];

  const fontSizes = [
    { value: '10px', label: 'Liten' },
    { value: '12px', label: 'Mellan' },
    { value: '14px', label: 'Stor' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    getFiltersFromStorage();
  }, []);

  useEffect(() => {
    if (tenderManagerToolList.length > 0) {
      applyFilters();
    }
  }, [filterChips, showUnpublished, tenderManagerToolList]);

  useEffect(() => {
    const isEqual = JSON.stringify(savedFilters) === JSON.stringify(filterChips);
    setShowSaveFilters(!isEqual);
  }, [filterChips, savedFilters]);

  const loadData = async () => {
    try {
      // Load tags
      const tagsData = await getAllTags();
      setTags(tagsData);

      // Load tenderings
      setLoadingTenderings(true);
      const tenderingsData = await getTenderingDocuments();
      setTenderings(tenderingsData);
      setLoadingTenderings(false);

      // Load tenders
      setLoadingTenders(true);
      const tendersData = await getTenderManagerDocuments();
      setTenders(tendersData);
      setLoadingTenders(false);

      // Load users
      const usersData = await getUsersByModule(1);
      const formattedUsers = usersData.map((ud: any) => ud.user).map((user: User) => ({
        userID: user.userID,
        displayName: `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`,
        fullName: `${user.firstName} ${user.lastName}`,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        color: user.color || '#f05a22'
      }));
      setUsers(formattedUsers);

      // Process tenders
      const processedTenders = tendersData.map(tender => ({
        ...tender,
        statuses: tender.statuses || (tender.hidden ? [1] : []),
        tenderingManager: tender.tenderingManager || [],
        planned: tender.planned || { date: '', text: '' },
        followedUp: tender.followedUp || { date: '', text: '' },
        prio: tender.prio || { prio: 5, text: '' },
      }));

      const sortedTenders = sortByAlphabet(processedTenders);
      setTenderManagerToolList(sortedTenders);
      setLoaded(true);
    } catch (error) {
      console.error(error);
      // Show error modal
    }
  };

  const getFiltersFromStorage = () => {
    const stored = localStorage.getItem('tenderingManagerFilters');
    const filters = stored ? JSON.parse(stored) : [];
    setFilterChips(filters);
    setSavedFilters(filters);
  };

  const saveFilters = () => {
    localStorage.setItem('tenderingManagerFilters', JSON.stringify(filterChips));
    setSavedFilters([...filterChips]);
    setShowSaveFilters(false);
  };

  const applyFilters = () => {
    let result = showUnpublished
      ? [...tenderManagerToolList]
      : tenderManagerToolList.filter(t => !t.unpublished);

    if (filterChips.length < 1) {
      setFilteredData(result);
      return;
    }

    filterChips.forEach((filter) => {
      const value = filter;
      
      // Include value
      if (value[0] === '+') {
        const searchValue = value.substring(1).toLowerCase();
        const matched: TenderManagerToolModel[] = [];
        result.forEach(t => {
          if (JSON.stringify(t).toLowerCase().includes(searchValue)) {
            matched.push(t);
          }
        });
        result = matched;
      }
      // Include value from specific column
      else if (value.includes('+')) {
        const values = value.split('+');
        const columnKey = Object.keys(columnMap).find(k => k.toLowerCase() === values[0].toLowerCase());
        const column = columnKey ? columnMap[columnKey] : undefined;
        if (!column) return;
        const filteredIds = getFilteredIds(values[1], column);
        const columnValue = values[1];
        const matched: TenderManagerToolModel[] = [];
        
        result.forEach(t => {
          if (filteredIds.length > 0) {
            filteredIds.forEach(id => {
              if ((JSON.stringify((t as any)[column]) ?? '').toLowerCase().includes(id)) {
                matched.push(t);
              }
            });
          } else {
            const temp = (JSON.stringify((t as any)[column]) ?? '').toLowerCase();
            if (column === 'prio' && t.prio.prio === Number(columnValue)) {
              matched.push(t);
            } else if (column !== 'prio' && temp.includes(columnValue.toLowerCase())) {
              matched.push(t);
            }
          }
        });
        result = matched;
      }
      // Exclude value
      else if (value[0] === '-') {
        const searchValue = value.substring(1).toLowerCase();
        result = result.filter(t => !JSON.stringify(t).toLowerCase().includes(searchValue));
      }
      // Exclude value from specific column
      else if (value.includes('-')) {
        const values = value.split('-');
        const columnKey = Object.keys(columnMap).find(k => k.toLowerCase() === values[0].toLowerCase());
        const column = columnKey ? columnMap[columnKey] : undefined;
        if (!column) return;
        const filteredIds = getFilteredIds(values[1], column);
        const columnValue = values[1];
        
        if (filteredIds.length > 0) {
          filteredIds.forEach(id => {
            result = result.filter(t => !(JSON.stringify((t as any)[column]) ?? '').toLowerCase().includes(id));
          });
        } else {
          result = result.filter(t => !(JSON.stringify((t as any)[column]) ?? '').toLowerCase().includes(columnValue.toLowerCase()));
        }
      }
      // Default to include
      else {
        const searchValue = value.toLowerCase();
        const matched: TenderManagerToolModel[] = [];
        result.forEach(t => {
          if (JSON.stringify(t).toLowerCase().includes(searchValue)) {
            matched.push(t);
          }
        });
        result = matched;
      }
    });

    // Remove duplicates
    const uniqueResult = result.filter((tender, index, self) =>
      index === self.findIndex(t => t.tenderID === tender.tenderID)
    );

    setFilteredData(uniqueResult);
  };

  const getFilteredIds = (filter: string, column: string): string[] => {
    if (column === 'tenderingManager') {
      const filteredUsers = users.filter(u => u.fullName.toLowerCase().includes(filter.toLowerCase()));
      return filteredUsers.map(u => u.userID.toString());
    } else if (column === 'statuses') {
      const filteredTags = tags.filter(t => t.tagName.toLowerCase().includes(filter.toLowerCase()));
      return filteredTags.map(t => t.tagID.toString());
    }
    return [];
  };

  const sortByAlphabet = (tendersList: TenderManagerToolModel[]): TenderManagerToolModel[] => {
    return [...tendersList].sort((a, b) => {
      const textA = a.tenderName;
      const textB = b.tenderName;
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  };

  const getUser = (id: number): User | undefined => {
    return users.find(u => u.userID === id);
  };

  const getTags = (tagIDs: number[]): TagModel[] => {
    if (tagIDs) {
      return tags.filter(t => tagIDs.includes(t.tagID));
    }
    return [];
  };

  const getRowClassName = (tenderID: number): string => {
    const tender = tenderManagerToolList.find(t => t.tenderID === tenderID);
    if (!tender) return '';
    
    const unpublishedTag = tags.find(t => t.tagName === 'Avpublicerad');
    
    if (tender.statuses.includes(1)) {
      return 'bg-red-50';
    } else if (unpublishedTag && tender.statuses.includes(unpublishedTag.tagID)) {
      return 'bg-gray-300';
    }
    return '';
  };

  const openModal = (tender: TenderManagerToolModel, option?: string) => {
    setSelectedTender(tender);
    setModalOption(option);
    setShowTenderDrawer(true);
  };

  const openContactModal = (tender: TenderManagerToolModel) => {
    setSelectedTender(tender);
    setShowContactModal(true);
  };

  const openFilterModal = (column: string) => {
    setFilterColumn(column);
    setShowFilterModal(true);
  };

  const handleFilterModalClose = (filterValue?: string) => {
    if (filterValue) {
      setFilterChips([...filterChips, filterValue]);
    }
    setShowFilterModal(false);
  };

  const openMap = () => {
    const toURL = filteredData.map(tender => {
      return `${tender.tenderName[0].substring(0, 25)},+${tender.adress[0].substring(0, 35)}`;
    });
    window.open(`https://www.google.com/maps/dir/${toURL.join('/').replace(/ /g, '+')}`, '_blank');
  };

  const handleTenderManagerChange = (updatedTenderManager: TenderManagerToolModel) => {
    // Update the tender in the list
    setTenderManagerToolList(prevList => 
      prevList.map(tender => 
        tender.tenderID === updatedTenderManager.tenderID ? updatedTenderManager : tender
      )
    );
  };

  const handleFilterAdd = (filter: string) => {
    if (filter && !filterChips.includes(filter)) {
      setFilterChips([...filterChips, filter]);
    }
  };

  const openPrioNoteDialog = (tender: TenderManagerToolModel) => {
    setSelectedTenderForPrio(tender);
    setShowPrioNoteDialog(true);
  };

  const handlePrioNoteSave = async (note: any) => {
    if (!selectedTenderForPrio) return;

    try {
      // Create the note
      const fullTender = tenders.find(t => t.tenderID === selectedTenderForPrio.tenderID);
      if (fullTender) {
        await createNote(fullTender.tenderID, note);
      }

      // Update prio from note data
      if (note.prio) {
        const updatedTool = {
          ...selectedTenderForPrio,
          prio: { prio: note.prio, text: note.noteBody },
        };
        
        await updateTenderManagerTool(updatedTool);
        handleTenderManagerChange(updatedTool);
      }

      setShowPrioNoteDialog(false);
      setSelectedTenderForPrio(null);
    } catch (error) {
      console.error('Error saving prio note:', error);
    }
  };

  const getPrioInitialNoteData = () => {
    if (!selectedTenderForPrio) return undefined;

    return {
      noteDate: new Date().toISOString(),
      isNotePublic: false,
      noteSubject: 'Prio',
      noteHeader: `${selectedTenderForPrio.tenderName} Prio`,
      noteBody: selectedTenderForPrio.prio?.text || '',
    };
  };

  const getPrioText = (text: any): string => {
    if (typeof text === 'string') {
      const cleaned = text.replace('&nbsp;', '');
      return cleaned.replace(/<\/?[^>]+(>|$)/g, '');
    }
    return '';
  };

  const handleAddFilterChip = (value: string) => {
    if (value && !filterChips.includes(value)) {
      setFilterChips([...filterChips, value]);
    }
    setFilterInputValue('');
  };

  const handleRemoveFilterChip = (chipToRemove: string) => {
    setFilterChips(filterChips.filter(chip => chip !== chipToRemove));
  };

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue = (a as any)[sortColumn];
      let bValue = (b as any)[sortColumn];

      if (sortColumn === 'prio') {
        aValue = a.prio?.prio || 5;
        bValue = b.prio?.prio || 5;
      } else if (sortColumn === 'planned') {
        aValue = a.planned?.date || '';
        bValue = b.planned?.date || '';
      } else if (sortColumn === 'followedUp') {
        aValue = a.followedUp?.date || '';
        bValue = b.followedUp?.date || '';
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const comparison = String(aValue).localeCompare(String(bValue));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortColumn, sortDirection]);

  if (!loaded) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Spinner size="extra-large" />
        {loadingTenderings && <p className="mt-4">Laddar Ramavtal...</p>}
        {loadingTenders && <p className="mt-4">Laddar Avtalsparter...</p>}
      </div>
    );
  }

  return (
    <div className="bg-[#ebebeb]">
      <div className="h-[130px] bg-gradient-to-r rounded from-orange-700 to-yellow-500">

      </div>
      <div className="bg-white w-full max-w-[1480px] m-auto flex flex-col gap-5 pt-9 p-5 rounded -mt-20">
        <h5 className="text-lg font-semibold mb-4">
          Filter ({sortedData.length} st verksamheter):
        </h5>

        <div className="flex items-center gap-4 mb-4">
        {/* Filter Chips Input */}
        <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2 flex-grow">
          {filterChips.map((chip, index) => {
            const chipColorClass = chip.startsWith('+')
              ? 'bg-green-100 text-green-700'
              : chip.startsWith('-')
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-700';

            return (
              <div key={index} className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${chipColorClass}`}>
                <span>{chip}</span>
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={() => handleRemoveFilterChip(chip)}
                />
              </div>
            );
          })}
          <Input
            type="text"
            value={filterInputValue}
            onChange={(e, data) => setFilterInputValue(data.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddFilterChip(filterInputValue);
              }
            }}
            placeholder="Add filter..."
            className="flex-grow min-w-[200px]"
            appearance="underline"
          />
        </div>

        <Button appearance="secondary" onClick={() => setShowColumnPanel(true)}>
          Visa/Dölj kolumner
        </Button>

        <Button
          appearance="secondary"
          onClick={() => setShowUnpublished(!showUnpublished)}
        >
          {showUnpublished ? 'Dölj avpublicerade' : 'Visa avpublicerade'}
        </Button>

        <Tooltip
          content="Det krävs färre än 20 verksamheter för att kunna visa kartan."
          relationship="label"
          withArrow
        >
          <Button
            appearance="secondary"
            disabled={sortedData.length > 20}
            onClick={openMap}
          >
            Visa karta
          </Button>
        </Tooltip>
      </div>

      {/* {showSaveFilters && (
        <Button appearance="primary" onClick={saveFilters} className="mb-4">
          Spara filter
        </Button>
      )} */}

      <div className="flex items-center gap-4 mb-4">
        <Label>Textstorlek:</Label>
        <RadioGroup
          value={selectedFontSize}
          onChange={(_, data) => setSelectedFontSize(data.value)}
          layout="horizontal"
        >
          {fontSizes.map((font) => (
            <Radio
              key={font.value}
              value={font.value}
              label={font.label}
              style={{ fontSize: font.value }}
            />
          ))}
        </RadioGroup>
      </div>

      <div className="overflow-auto shadow-lg">
        <Table size="small" sortable>
          <TableHeader>
            <TableRow>
              {columnDefinitions
                .filter((col) => !hiddenColumns.includes(col.key))
                .map((col) => (
                  <TableHeaderCell
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{col.name}</span>
                      {sortColumn === col.key &&
                        (sortDirection === 'asc' ? (
                          <ChevronUpRegular />
                        ) : (
                          <ChevronDownRegular />
                        ))}
                      <Button
                        appearance="transparent"
                        size="small"
                        icon={<FilterRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openFilterModal(col.name);
                        }}
                      />
                    </div>
                  </TableHeaderCell>
                ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item) => {
              const rowClassName = getRowClassName(item.tenderID);
              const fullRowClassName = `cursor-pointer hover:bg-gray-50 ${rowClassName}`.trim();

              return (
                <TableRow
                  key={item.tenderID}
                  className={fullRowClassName}
                  onClick={() => openModal(item)}
                >
                  {columnDefinitions
                    .filter((col) => !hiddenColumns.includes(col.key))
                    .map((col) => (
                      <TableCell key={col.key}>
                        <TableCellLayout>
                          {renderCell(item, col.key)}
                        </TableCellLayout>
                      </TableCell>
                    ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Column Visibility Dialog */}
      <Dialog open={showColumnPanel} onOpenChange={(_, data) => setShowColumnPanel(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Visa/Dölj kolumner</DialogTitle>
            <DialogContent>
              <div className="p-6 flex flex-col gap-2">
                {columnDefinitions.map((col) => (
                  <Checkbox
                    key={col.key}
                    label={col.name}
                    checked={!hiddenColumns.includes(col.key)}
                    onChange={(_, data) => {
                      if (data.checked) {
                        setHiddenColumns(hiddenColumns.filter((c) => c !== col.key));
                      } else {
                        setHiddenColumns([...hiddenColumns, col.key]);
                      }
                    }}
                  />
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setShowColumnPanel(false)}>
                Stäng
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Modals */}
      {showTenderDrawer && selectedTender && (
        <TenderSettingsDrawer
          open={showTenderDrawer}
          onClose={() => setShowTenderDrawer(false)}
          partialTender={tenders.find((t) => t.tenderID === selectedTender.tenderID)}
          tenderManager={selectedTender}
          users={users}
          tags={tags}
          tab="tenderManager"
          onTenderManagerChange={handleTenderManagerChange}
          onFilterAdd={handleFilterAdd}
        />
      )}

      <TenderContactDrawer
        isOpen={showContactModal}
        onDismiss={() => setShowContactModal(false)}
        tender={selectedTender ? tenders.find((t) => t.tenderID === selectedTender.tenderID) : undefined}
        tenders={tenders}
        tendering={selectedTender ? tenderings.find((t) => t.tenderingID === selectedTender.tenderingID) : undefined}
        tags={tags}
      />

      {showFilterModal && (
        <TenderingManagerFilterDialog
          onClose={() => setShowFilterModal(false)}
          open={showFilterModal}
          onDismiss={handleFilterModalClose}
          column={filterColumn}
        />
      )}

      {showPrioNoteDialog && selectedTenderForPrio && (
        <NewNote
          open={showPrioNoteDialog}
          onClose={() => {
            setShowPrioNoteDialog(false);
            setSelectedTenderForPrio(null);
          }}
          onSave={handlePrioNoteSave}
          initialData={getPrioInitialNoteData()}
          actionType="prio"
        />
      )}
      </div>
    </div>
  );

  function renderCell(item: TenderManagerToolModel, columnKey: string) {
    const style = { fontSize: selectedFontSize };

    switch (columnKey) {
      case 'tenderingManager':
        return (
          <div className="flex gap-0.5">
            {item.tenderingManager?.map((mgId) => {
              const user = getUser(mgId);
              return user ? (
                <div
                  key={mgId}
                  className="inline-flex w-[25px] h-[25px] rounded-full text-[10px] text-white items-center justify-center mr-1"
                  style={{ backgroundColor: user.color }}
                >
                  {user.displayName}
                </div>
              ) : null;
            })}
          </div>
        );
      case 'planned':
        return (
          <span style={style}>
            {item.planned?.date} {item.planned?.text}
          </span>
        );
      case 'followedUp':
        return (
          <span style={style}>
            {item.followedUp?.date} {item.followedUp?.text}
          </span>
        );
      case 'contactName':
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openContactModal(item);
            }}
            className="text-blue-600 hover:underline"
            style={style}
          >
            {item.contactName}
          </a>
        );
      case 'prio':
        return (
          <Tooltip
            content={getPrioText(item.prio?.text)}
            relationship="label"
            withArrow
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openPrioNoteDialog(item);
              }}
              className="text-blue-600 hover:underline pl-2"
              style={style}
            >
              {item.prio?.prio}
            </a>
          </Tooltip>
        );
      case 'statuses':
        return (
          <div className="flex flex-wrap gap-1">
            {getTags(item.statuses).map((tag) => (
              <Badge key={tag.tagID} className="my-1">
                {tag.tagName}
              </Badge>
            ))}
          </div>
        );
      default:
        const value = item[columnKey as keyof TenderManagerToolModel];
        const displayValue = typeof value === 'object' && value !== null 
          ? value.toString()
          : String(value ?? '');
        return <span style={style}>{displayValue}</span>;
    }
  }
};

export default TenderingManagerTool;
